import React, { useRef } from 'react'
import { Menu } from "antd";
import styled from 'styled-components'
import { useClickAway } from 'ahooks';
import {
    AppstoreOutlined,
    ChromeOutlined,
    CopyOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    FileOutlined,
    FolderOpenOutlined,
    HighlightOutlined,
    ReloadOutlined,
    ScissorOutlined,
    ShareAltOutlined,
    SnippetsOutlined,
    StarOutlined,
    TagOutlined
} from '@ant-design/icons';
import { canOpenInApp, FILE_MANAGEMENT_POPUP_Z_INDEX } from './utils';

const StyleMenu = styled(Menu)`
    border-radius: 10px !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06);
    padding: 6px !important;
    border: 1px solid #e8eaed;
    min-width: 200px;

    .ant-menu-item {
        margin: 0 !important;
        padding: 7px 12px !important;
        border-radius: 6px;
        line-height: 26px;
        font-size: 13px;
        transition: all 0.12s ease;
        color: #333;
    }
    .ant-menu-item:hover {
        background-color: rgba(0, 121, 191, 0.07) !important;
        color: rgb(0, 121, 191);
    }
    .ant-menu-item .anticon {
        font-size: 15px;
        opacity: 0.65;
    }
    .ant-menu-item:hover .anticon {
        opacity: 1;
    }
    .ant-menu-item-divider {
        margin: 4px 8px !important;
        border-color: #f0f0f0;
    }
`

const getMenuMap = () => {
  const D = { type: 'divider' };
  return [
    [() => true, { key: 'OpenFolder', icon: <FolderOpenOutlined />, label: 'Open Folder', visible: (sel) => sel.length === 1 && sel[0].isFolder && !sel[0].isSystem }],
    [() => true, { key: 'Open', icon: <FileOutlined />, label: 'Open', visible: (sel) => sel.length === 1 && !sel[0].isFolder && !sel[0].isSystem }],
    [() => true, { key: 'OpenInBrowser', icon: <ChromeOutlined />, label: 'Open in Browser', visible: (sel) => sel.length === 1 && canOpenInApp(sel[0]) }],
    [() => true, { key: 'OpenInApp', icon: <AppstoreOutlined />, label: 'Open in App', visible: (sel) => sel.length === 1 && canOpenInApp(sel[0]) }],
    [() => true, D],
    // R1：System 子树只读——Edit/Rename/EditTags/Delete/MoveTo/Cut 隐藏；Copy（复制出）保留
    [() => true, { key: 'Edit', icon: <EditOutlined />, label: 'Edit', visible: (sel, p) => p.canWrite && sel.length === 1 && !sel[0].isSystem && !sel[0].isVirtual && !(sel[0].isFolder && sel[0].is_system_managed) && !p.isSystemReadonly?.(sel[0]) }],
    [() => true, { key: 'Rename', icon: <HighlightOutlined />, label: 'Rename', visible: (sel, p) => p.canWrite && sel.length === 1 && !sel[0].isSystem && !sel[0].isVirtual && !(sel[0].isFolder && sel[0].is_system_managed) && !p.isSystemReadonly?.(sel[0]) }],
    [() => true, { key: 'EditTags', icon: <TagOutlined />, label: 'Edit Tags', visible: (sel, p) => p.canWrite && sel.length === 1 && !sel[0].isSystem && !sel[0].isVirtual && !(sel[0].isFolder && sel[0].is_system_managed) && !p.isSystemReadonly?.(sel[0]) }],
    [() => true, D],
    [() => true, { key: 'Download', icon: <DownloadOutlined />, label: 'Download', visible: (sel) => sel.every(r => !r.isFolder) }],
    [() => true, { key: 'Refresh', icon: <ReloadOutlined />, label: 'Refresh', visible: () => true }],
    [() => true, { key: 'Delete', icon: <DeleteOutlined />, label: 'Delete', visible: (sel, p) => sel.every(r => !r.isSystem && !r.isVirtual && !(r.isFolder && r.is_system_managed) && !p.isSystemReadonly?.(r)) && (p.canFullAccess || sel.every(r => r.createdby === p.userId)) }],
    [() => true, D],
    [() => true, { key: 'CopyLink', icon: <ShareAltOutlined />, label: 'Copy Link', visible: (sel) => sel.length === 1 && !sel[0].isFolder }],
    [() => true, { key: 'MoveTo', icon: <ScissorOutlined />, label: 'Move To', visible: (sel, p) => p.canWrite && sel.length === 1 && !sel[0].isSystem && !sel[0].isVirtual && !(sel[0].isFolder && sel[0].is_system_managed) && !p.isSystemReadonly?.(sel[0]) }],
    [() => true, { key: 'Copy', icon: <CopyOutlined />, label: 'Copy', visible: (sel, p) => p.canWrite && sel.length > 0 && sel.every(r => !r.isSystem && !r.isVirtual && !(r.isFolder && r.is_system_managed)) }],
    [() => true, { key: 'Cut', icon: <ScissorOutlined />, label: 'Cut', visible: (sel, p) => p.canWrite && sel.length > 0 && sel.every(r => !r.isSystem && !r.isVirtual && !(r.isFolder && r.is_system_managed) && !p.isSystemReadonly?.(r)) }],
    [() => true, { key: 'Paste', icon: <SnippetsOutlined />, label: 'Paste', visible: (sel, p) => Boolean(p.canPaste) }],
    [() => true, D],
    [() => true, { key: 'ToggleFavorite', icon: <StarOutlined />, label: null, visible: (sel) => sel.length === 1 }],
  ];
};

const ContextMenu = ({ x, y, isContextMenuOpen, setIsContextMenuOpen, selectedRowKeys, onMenuItemClick, canWrite, canFullAccess, userId, canPaste, isFavorited, isSystemReadonly }) => {

    const ref = useRef(null)

    useClickAway(() => {
        setIsContextMenuOpen(false)
    }, () => document.getElementById("file-context-menu"))


    const permissions = { canWrite, canFullAccess, userId, canPaste, isFavorited, isSystemReadonly };
    const menuItems = getMenuMap()
      .map(([, item]) => {
        if (item.type === 'divider') return item;
        if (item.visible && !item.visible(selectedRowKeys, permissions)) return null;
        if (item.key === 'ToggleFavorite') {
          return { ...item, label: isFavorited ? 'Remove from Favorites' : 'Add to Favorites' };
        }
        const { visible, ...rest } = item;
        return rest;
      })
      .filter(Boolean);
    const menuHeight = menuItems.reduce((h, item) => h + (item.type === 'divider' ? 16 : 40), 0);
    const style = {
        position: 'fixed',
        left: x,
        // 防止菜单超出窗口
        top: (y + menuHeight > window.innerHeight) ? window.innerHeight - menuHeight : y,
        zIndex: FILE_MANAGEMENT_POPUP_Z_INDEX,
        display: isContextMenuOpen ? 'block' : 'none',
    };
    return <StyleMenu selectedKeys={[]} id="file-context-menu" ref={ref} style={style} items={menuItems} onClick={onMenuItemClick} />
}

export default ContextMenu